import type { SaveSlot } from "../persistence/GameStorage";

const STYLE = `
  #menu-overlay { position: fixed; inset: 0; background: #1d352966; display: none; z-index: 100; justify-content: center; align-items: center; font: 15px system-ui, sans-serif; color: #314338; }
  #menu-overlay.show { display: flex; }
  #menu-card { background: #fffdf5; border: 2px solid #b9d8aa; border-radius: 20px; box-shadow: 0 12px 32px #1d352966; padding: 28px 36px; max-width: 400px; width: 90%; max-height: 80vh; overflow-y: auto; }
  #menu-card h2 { margin: 0 0 20px; font-size: 20px; color: #31583b; text-align: center; }
  #menu-card .menu-btn { display: block; width: 100%; padding: 12px 16px; margin-bottom: 10px; background: #eef7e9; color: #314338; border: 2px solid #b9d8aa; border-radius: 14px; font-size: 15px; font-weight: 600; cursor: pointer; text-align: left; }
  #menu-card .menu-btn:hover { background: #dff2d8; }
  #menu-card .menu-btn.danger { background: #fff0ed; border-color: #e5a79d; color: #7a2f2f; }
  #menu-card .menu-btn.danger:hover { background: #f8d7d1; }
  #menu-card .menu-section { margin-top: 18px; padding-top: 14px; border-top: 1px solid #b9d8aa; }
  #menu-card .menu-section h3 { margin: 0 0 10px; font-size: 13px; color: #42694b; text-transform: uppercase; letter-spacing: .5px; }
  #menu-card .save-list { max-height: 200px; overflow-y: auto; }
  #menu-card .save-item { display: flex; align-items: center; gap: 10px; padding: 10px 12px; background: #eef7e9; border: 1px solid #b9d8aa; border-radius: 10px; margin-bottom: 6px; cursor: pointer; transition: background .15s; }
  #menu-card .save-item:hover { background: #dff2d8; }
  #menu-card .save-item .save-info { flex: 1; min-width: 0; }
  #menu-card .save-item .save-name { font-size: 14px; color: #314338; }
  #menu-card .save-item .save-time { font-size: 12px; color: #42694b; margin-top: 2px; }
  #menu-card .save-item .save-delete { background: none; border: none; color: #a54a42; cursor: pointer; font-size: 16px; padding: 4px 8px; }
  #menu-card .save-item .save-delete:hover { color: #7a2f2f; }
  #menu-card .empty-saves { color: #42694b; font-size: 13px; text-align: center; padding: 12px; }
  #confirm-overlay { position: fixed; inset: 0; background: #1d352966; display: none; z-index: 110; justify-content: center; align-items: center; font: 15px system-ui, sans-serif; color: #314338; }
  #confirm-overlay.show { display: flex; }
  #confirm-card { background: #fffdf5; border: 2px solid #b9d8aa; border-radius: 20px; box-shadow: 0 12px 32px #1d352966; padding: 24px 28px; max-width: 380px; width: 90%; text-align: center; }
  #confirm-card p { margin: 0 0 18px; line-height: 1.5; }
  #confirm-card .btn-row { display: flex; gap: 10px; justify-content: center; }
  #confirm-card button { padding: 8px 20px; border-radius: 999px; font-size: 15px; font-weight: 600; cursor: pointer; border: 2px solid #b9d8aa; }
  #confirm-card .btn-cancel { background: #eef7e9; color: #314338; }
  #confirm-card .btn-cancel:hover { background: #dff2d8; }
  #confirm-card .btn-danger { background: #fff0ed; color: #7a2f2f; border-color: #e5a79d; }
  #confirm-card .btn-danger:hover { background: #f8d7d1; }
`;

const MENU_HTML = `
  <div id="menu-card">
    <h2>Game Menu</h2>
    <button class="menu-btn" id="menu-save">💾 Save Game</button>
    <div class="menu-section">
      <h3>Saved Games</h3>
      <div id="save-list" class="save-list"></div>
    </div>
    <button class="menu-btn danger" id="menu-restart">↺ Restart Game</button>
  </div>
`;

const CONFIRM_HTML = `
  <div id="confirm-card">
    <p>Restart the game? All unsaved progress will be lost.</p>
    <div class="btn-row">
      <button class="btn-cancel" id="confirm-cancel">Cancel</button>
      <button class="btn-danger" id="confirm-restart">Restart</button>
    </div>
  </div>
`;

export interface GameMenuOverlay {
  toggle(): void;
  close(): void;
  showConfirm(): void;
  closeConfirm(): void;
  setSaveList(slots: readonly SaveSlot[]): void;
  onSave(handler: () => void): void;
  onLoad(handler: (slotId: string) => void): void;
  onDeleteSlot(handler: (slotId: string) => void): void;
  onRestart(handler: () => void): void;
  readonly menuElement: HTMLElement;
  readonly confirmElement: HTMLElement;
}

function formatTime(timestamp: number): string {
  const d = new Date(timestamp);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
    " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export function createGameMenuOverlay(): GameMenuOverlay {
  const style = document.createElement("style");
  style.textContent = STYLE;
  document.head.appendChild(style);

  const menuEl = document.createElement("div");
  menuEl.id = "menu-overlay";
  menuEl.innerHTML = MENU_HTML;
  document.body.appendChild(menuEl);

  const confirmEl = document.createElement("div");
  confirmEl.id = "confirm-overlay";
  confirmEl.innerHTML = CONFIRM_HTML;
  document.body.appendChild(confirmEl);

  const saveList = menuEl.querySelector<HTMLElement>("#save-list")!;

  let saveHandler: (() => void) | undefined;
  let loadHandler: ((slotId: string) => void) | undefined;
  let deleteSlotHandler: ((slotId: string) => void) | undefined;
  let restartHandler: (() => void) | undefined;

  menuEl.addEventListener("click", (e) => {
    if (e.target === menuEl) menuEl.classList.remove("show");
  });
  menuEl.querySelector("#menu-save")!.addEventListener("click", () => {
    saveHandler?.();
  });
  menuEl.querySelector("#menu-restart")!.addEventListener("click", () => {
    menuEl.classList.remove("show");
    confirmEl.classList.add("show");
  });

  confirmEl.addEventListener("click", (e) => {
    if (e.target === confirmEl) confirmEl.classList.remove("show");
  });
  confirmEl.querySelector("#confirm-cancel")!.addEventListener("click", () => {
    confirmEl.classList.remove("show");
  });
  confirmEl.querySelector("#confirm-restart")!.addEventListener("click", () => {
    confirmEl.classList.remove("show");
    restartHandler?.();
  });

  return {
    toggle() { menuEl.classList.toggle("show"); },
    close() { menuEl.classList.remove("show"); },
    showConfirm() { confirmEl.classList.add("show"); },
    closeConfirm() { confirmEl.classList.remove("show"); },
    setSaveList(slots) {
      saveList.innerHTML = "";
      if (slots.length === 0) {
        saveList.innerHTML = '<div class="empty-saves">No saved games</div>';
        return;
      }
      for (const slot of [...slots].reverse()) {
        const item = document.createElement("div");
        item.className = "save-item";
        item.innerHTML = `
          <div class="save-info">
            <div class="save-name">${slot.name}</div>
            <div class="save-time">${formatTime(slot.timestamp)}</div>
          </div>
          <button class="save-delete" title="Delete">✕</button>
        `;
        item.querySelector(".save-info")!.addEventListener("click", () => {
          loadHandler?.(slot.id);
        });
        item.querySelector(".save-delete")!.addEventListener("click", (e) => {
          e.stopPropagation();
          deleteSlotHandler?.(slot.id);
        });
        saveList.appendChild(item);
      }
    },
    onSave(handler) {
      saveHandler = handler;
    },
    onLoad(handler) {
      loadHandler = handler;
    },
    onDeleteSlot(handler) {
      deleteSlotHandler = handler;
    },
    onRestart(handler) {
      restartHandler = handler;
    },
    get menuElement() { return menuEl; },
    get confirmElement() { return confirmEl; },
  };
}
